import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form, ProgressBar, Row, Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport, faMoneyBillTransfer, faRotateLeft, faTriangleExclamation, faTruckFast, faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import { usePaypalJobs } from "../../contexts/PaypalJobsContext";

const SHEET_GROUP = "tiktok_lising_sheets";
const STORAGE_KEY = "paypal_tools_selected_sheet";
const ACCOUNTS_STORAGE_KEY = "paypal_tools_selected_accounts";

// Map a UI "kind" to the server-side job type. Used for both submit and
// progress lookup so the inline cards keep showing the latest run state.
const KIND_TO_TYPE = {
  payments: "EXPORT_PAYMENTS",
  refunds: "EXPORT_REFUNDS",
  tracking: "EXPORT_TRACKING",
  addTracking: "ADD_TRACKING",
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthsAgoIso = (m) => {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
};

const PaypalTools = ({ accountList }) => {
  const { lastJobByType, submitJob, cancelJob } = usePaypalJobs();

  const [sheetsList, setSheetsList] = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [loadingSheets, setLoadingSheets] = useState(true);

  const [accountIds, setAccountIds] = useState(() => {
    try {
      const raw = localStorage.getItem(ACCOUNTS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [startDate, setStartDate] = useState(monthsAgoIso(1));
  const [endDate, setEndDate] = useState(todayIso());

  // Per-kind active job lookup. A job is "active" while queued or running.
  const jobByKind = useMemo(() => {
    const out = {};
    Object.entries(KIND_TO_TYPE).forEach(([kind, type]) => {
      out[kind] = lastJobByType[type] || null;
    });
    return out;
  }, [lastJobByType]);

  const isKindActive = (kind) => {
    const j = jobByKind[kind];
    return j && (j.status === "queued" || j.status === "running");
  };
  const anyActive = Object.keys(KIND_TO_TYPE).some(isKindActive);

  useEffect(() => {
    const fetchSheets = async () => {
      try {
        const response = await Utils._apiRequest({
          path: Enums.endpoints.options.LIST,
          type: "GET",
        });
        const options = response.data || [];
        const list = options.filter((o) => o.group === SHEET_GROUP);
        setSheetsList(list);
        if (selectedSheetId && !list.some((s) => s.key === selectedSheetId)) {
          setSelectedSheetId("");
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.log("fetch sheets:", e.message);
      } finally {
        setLoadingSheets(false);
      }
    };
    fetchSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!accountList?.length) return;
    const valid = accountList.map((a) => a._id);
    const filtered = accountIds.filter((id) => valid.includes(id));
    if (filtered.length !== accountIds.length) {
      setAccountIds(filtered.length ? filtered : valid);
      return;
    }
    if (!accountIds.length) setAccountIds(valid);
  }, [accountList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist account selection to localStorage on every change.
  useEffect(() => {
    try {
      if (accountIds.length) {
        localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accountIds));
      } else {
        localStorage.removeItem(ACCOUNTS_STORAGE_KEY);
      }
    } catch { /* ignore quota errors */ }
  }, [accountIds]);

  const accountById = useMemo(() => {
    const map = {};
    (accountList || []).forEach((a) => { map[a._id] = a; });
    return map;
  }, [accountList]);
  const accountLabel = (accId) => {
    const a = accountById[accId];
    return a ? `${a.accountName} (${a.meta?.mode || 'live'})` : accId;
  };
  const toggleAccount = (id) => {
    setAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllAccounts = () => {
    if (!accountList?.length) return;
    if (accountIds.length === accountList.length) setAccountIds([]);
    else setAccountIds(accountList.map((a) => a._id));
  };

  const handleSheetChange = (e) => {
    const value = e.target.value;
    setSelectedSheetId(value);
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  };


  const validateInputs = (forSheet = true) => {
    if (!accountIds.length) { toast.warning("Chọn ít nhất 1 tài khoản PayPal"); return false; }
    if (forSheet && !selectedSheetId) { toast.warning("Chọn Google Sheet để lưu kết quả"); return false; }
    if (forSheet && (!startDate || !endDate)) { toast.warning("Chọn khoảng ngày"); return false; }
    return true;
  };

  /**
   * Helper: enqueue a background job and surface a toast. The Jobs panel at
   * the bottom of the page (and the inline progress card here) update as the
   * worker reports progress, so this returns immediately after submit.
   */
  const enqueue = async (kind, payload, label, requiresSheet = true) => {
    if (requiresSheet && !validateInputs(true)) return;
    if (!requiresSheet && !validateInputs(false)) return;
    if (isKindActive(kind)) {
      toast.info(`${label}: đã có job đang chạy/đợi`);
      return;
    }
    try {
      await submitJob(KIND_TO_TYPE[kind], payload, label);
      toast.success(`${label}: đã đưa vào hàng đợi`);
    } catch (err) {
      // submitJob already toasts on failure; nothing else to do.
    }
  };

  /* -------------------- PAYMENTS -------------------- */
  const runPayments = () => enqueue(
    "payments",
    { accountIds, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate },
    "Export Payments",
  );

  /* -------------------- REFUNDS -------------------- */
  const runRefunds = () => enqueue(
    "refunds",
    { accountIds, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate },
    "Export Refunds",
  );

  /* -------------------- TRACKING (auto, account detected per row) -------------------- */
  const runTracking = () => {
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet để lưu kết quả"); return; }
    if (isKindActive("tracking")) { toast.info("Export Tracking: đã có job đang chạy"); return; }
    submitJob("EXPORT_TRACKING", { sheetId: selectedSheetId, sheetName: "Payments" }, "Export Tracking")
      .then(() => toast.success("Export Tracking: đã đưa vào hàng đợi"))
      .catch(() => {});
  };

  /* -------------------- ADD TRACKING -------------------- */
  const runAddTracking = () => {
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet trước"); return; }
    if (isKindActive("addTracking")) { toast.info("Add Tracking: đã có job đang chạy"); return; }
    submitJob("ADD_TRACKING", { sheetId: selectedSheetId, sheetName: "Payments" }, "Add Tracking")
      .then(() => toast.success("Add Tracking: đã đưa vào hàng đợi"))
      .catch(() => {});
  };

  /* -------------------- Render helpers -------------------- */
  const renderProgress = (kind) => {
    const j = jobByKind[kind];
    if (!j) return null;

    const status = j.status;
    const variant = status === "error" ? "danger"
      : status === "done" ? "success"
      : status === "cancelled" ? "warning"
      : "info";
    const total = j.progress?.total || 0;
    const current = j.progress?.current || 0;
    const pct = total > 0
      ? Math.round((current / total) * 100)
      : (status === "running" ? 100 : (status === "done" ? 100 : 0));
    const msg = j.error || j.progress?.message || "";

    return (
      <div className="mt-2">
        <ProgressBar
          now={pct}
          striped={status === "running" || status === "queued"}
          animated={status === "running"}
          variant={variant}
          label={total > 0 ? `${current}/${total}` : ""}
          style={{ height: 14 }}
        />
        <div className="small mt-1 d-flex align-items-center gap-2">
          {status === "queued" && <Badge bg="secondary">Hàng đợi</Badge>}
          {status === "running" && <Badge bg="primary">Đang chạy</Badge>}
          {status === "done" && <Badge bg="success">Hoàn tất</Badge>}
          {status === "error" && <Badge bg="danger">Lỗi</Badge>}
          {status === "cancelled" && <Badge bg="warning">Đã hủy</Badge>}
          <span className={status === "error" ? "text-danger" : "text-muted"}>{msg}</span>
          {(status === "queued" || status === "running") && (
            <Button
              size="sm"
              variant="link"
              className="p-0 ms-auto text-danger small"
              onClick={() => cancelJob(j._id).then(() => toast.info("Đã yêu cầu hủy")).catch((e) => toast.error(e.message))}
            >
              <FontAwesomeIcon icon={faXmark} className="me-1" />Hủy
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderRunButton = (kind, label, variant, onClick) => {
    const active = isKindActive(kind);
    return (
      <Button size="sm" variant={variant} disabled={active} onClick={onClick}>
        {active ? (
          <><Spinner size="sm" animation="border" /> Đang chạy…</>
        ) : label}
      </Button>
    );
  };

  return (
    <div className="my-3">
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileExport} className="me-2" />
          <strong>Công cụ Export</strong>
        </Card.Header>
        <Card.Body>
          {accountList?.length === 0 && (
            <Alert variant="warning" className="py-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              Chưa có tài khoản PayPal nào. Hãy thêm tài khoản ở phía trên trước.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="small mb-1">
                    Tài khoản PayPal{" "}
                    <span className="text-muted">
                      ({accountIds.length}/{accountList?.length || 0})
                    </span>
                  </Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 small"
                    onClick={toggleSelectAllAccounts}
                    disabled={!accountList?.length || anyActive}
                  >
                    {accountIds.length === (accountList?.length || 0) ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                  </Button>
                </div>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: 110, overflowY: "auto", background: "#fff" }}
                >
                  {(accountList || []).length === 0 && (
                    <div className="small text-muted">Chưa có tài khoản</div>
                  )}
                  {(accountList || []).map((a) => (
                    <Form.Check
                      key={a._id}
                      type="checkbox"
                      id={`pp-acc-${a._id}`}
                      className="small"
                      checked={accountIds.includes(a._id)}
                      onChange={() => toggleAccount(a._id)}
                      disabled={anyActive}
                      label={`${a.accountName} (${a.meta?.mode || "live"})`}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Từ ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={anyActive}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Đến ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={anyActive}
                />
              </Form.Group>
            </Col>
            <Col md={2}></Col>

            <Col md={9}>
              <Form.Group>
                <Form.Label className="small">Google Sheet lưu kết quả</Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedSheetId}
                  onChange={handleSheetChange}
                  disabled={loadingSheets || anyActive}
                >
                  <option value="">-- Chọn sheet đã lưu --</option>
                  {sheetsList.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.values?.name || s.key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                as={Link}
                to="/settings#google-sheet"
                variant="dark"
                size="sm"
                className="w-100"
              >
                <FontAwesomeIcon icon={faGoogleDrive} className="me-2" />
                Quản lý Sheets
              </Button>
            </Col>
          </Row>

          <hr />

          <Row className="g-3">
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faMoneyBillTransfer} className="me-2 text-primary" />
                    Export Payments
                  </h6>
                  <p className="small text-muted mb-2">
                    Lấy giao dịch nhận tiền (T0006) từ PayPal, ghi vào tab <code>Payments</code>.
                  </p>
                  {renderRunButton("payments", "Export Payments", "primary", runPayments)}
                  {renderProgress("payments")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faRotateLeft} className="me-2 text-danger" />
                    Export Refunds
                  </h6>
                  <p className="small text-muted mb-2">
                    Lấy refund (gồm cả chargeback) và cập nhật vào cột <code>Refunded</code> của sheet <code>Payments</code> theo <code>Invoice</code>.
                  </p>
                  {renderRunButton("refunds", "Export Refunds", "danger", runRefunds)}
                  {renderProgress("refunds")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faTruckFast} className="me-2 text-info" />
                    Shipping Tracker
                    <Badge bg="info" className="ms-2">Auto</Badge>
                  </h6>
                  <p className="small text-muted mb-2">
                    <strong>Export</strong>: kéo tracking từ PayPal về sheet <code>Payments</code>.<br />
                    <strong>Add</strong>: đẩy tracking nhập tay từ sheet lên PayPal.
                  </p>
                  <Alert variant="info" className="py-1 px-2 small mb-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                    Tự xác định account theo cột <code>Account ID</code>. Dòng thiếu cột này sẽ bị bỏ qua — chạy <em>Export Payments</em> trước để bổ sung.
                  </Alert>
                  <div className="d-flex gap-2 flex-wrap">
                    {renderRunButton("tracking", "Export Tracking", "info", runTracking)}
                    {renderRunButton("addTracking", "Add Tracking", "success", runAddTracking)}
                  </div>
                  {renderProgress("tracking")}
                  {renderProgress("addTracking")}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaypalTools;
